import {Component, OnInit, Renderer2} from '@angular/core';
import {NavigationEnd, Router, RouterOutlet, RoutesRecognized} from '@angular/router';
import {HeaderWebComponent} from './shared/components/main-header/header-web/header-web.component';
import {Toast} from 'primeng/toast';
import {MessageService} from 'primeng/api';
import {MainPageComponent} from './modules/main-page/main-page.component';

import {Title} from '@angular/platform-browser';
import {LoggedInService} from './tinea-components/okta/services/loggedIn.service';
import {TranslateService} from '@ngx-translate/core';
import {Globals} from './shared/configuration/globals';
import {FullyLoggedInStateType} from './tinea-components/okta/enum/FullyLoggedInStateType';
import {environment} from '../environments/environment';
import {filter} from 'rxjs/operators';
import {PrimeNG} from 'primeng/config';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [
    HeaderWebComponent,
    MainPageComponent,
    Toast,
    RouterOutlet
]
})
export class AppComponent implements OnInit {
  public title = 'gnvert-analyse-front';
  public isExpanded = true;
  public isLoggedIn = false;
  public route!: string;
  public currentUrlSlug!: string;
  public previousUrl!: string;

  constructor(
    private router: Router,
    private renderer: Renderer2,
    private titleService: Title,
    private loggedInService: LoggedInService,
    private translate: TranslateService,
    private globals: Globals,
    private primeng: PrimeNG,
    private messageService: MessageService
  ) {

    // this language will be used as a fallback when a translation isn't found in the current language
    this.translate.setDefaultLang('en');
    this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.route = e.url;
      }
    });

    const languageStored = localStorage.getItem('language');

    if (languageStored) {
      this.translate.use(languageStored);
    } else {
      /*  If browser language not in the list, use default language. */
      const browserLanguage = this.translate.getBrowserLang();
      if (
        this.globals.languages.find((lang) => lang.name === browserLanguage) ===
        undefined
      ) {
        this.translate.use(this.translate.getDefaultLang());
        localStorage.setItem('language', this.translate.getDefaultLang());
      } else {
        this.translate.use(browserLanguage ?? '');
        localStorage.setItem('language', browserLanguage ?? '');
      }
    }

    // get url & set body class depending on space
    this.setBodyClass();
  }

  ngOnInit(): void {
    this.primeng.ripple.set(true);

    this.isLoggedinPropertyObserve();
  }

  toggleMenu() {
    this.isExpanded = !this.isExpanded;
  }

  async isLoggedinPropertyObserve() {
    // set property 'isLoggedin' on refresh page
    const isLoggedIn = await this.loggedInService.isLoggedIn();
    this.setLoggedInProperty(isLoggedIn);
    // subscribe to observer
    this.loggedInService.fullyLoggedInState$.subscribe(
      (isFullyLoggedIn: FullyLoggedInStateType) => {
        if (isFullyLoggedIn === FullyLoggedInStateType.SUCCESS) {
          this.setLoggedInProperty(true);
        } else {
          this.setLoggedInProperty(false);
        }
      }
    );
  }

  setBodyClass() {
    // take event that contains the final route,
    // otherwise irrelevant url slugs like type errors will become body class
    this.router.events
      .pipe(filter((e) => e instanceof RoutesRecognized))
      .subscribe((event: RoutesRecognized) => {
        if (this.previousUrl && this.previousUrl !== '/') {
          this.renderer.removeClass(
            document.body,
            this.previousUrl.split('/')[1]
          );
        }
        this.currentUrlSlug = event.urlAfterRedirects.split('/')[1];
        if (this.currentUrlSlug === '') {
          this.renderer.addClass(document.body, 'home');
        }

        if (this.currentUrlSlug) {
          this.renderer.removeClass(document.body, 'home');
          this.renderer.addClass(document.body, this.currentUrlSlug);
          // this.logger.log('Current url slug ', this.currentUrlSlug); // DEBUG
        }

        // set url for redirecting user after login (TODO: currently, avoiding sites list, to be changed when several sites view available)
        this.previousUrl = event.urlAfterRedirects;
        if (
          !this.previousUrl.includes('login') &&
          !this.previousUrl.includes('implicit/callback') &&
          this.previousUrl !== '/sites'
        ) {
          // this.logger.log('set previous url', this.globals.previousUrl); // DEBUG
        }
      });

    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.setTitle(this.currentUrlSlug);
      });
  }

  setLoggedInProperty(value: boolean) {
    this.isLoggedIn = value;
  }

  /**
   * Set the relevant title for each page, that will be visible in history.
   */
  public setTitle(newTitle: string) {
    let formattedTitle: string = newTitle;
    if (formattedTitle.includes('?')) {
      formattedTitle = formattedTitle.split('?')[0];
    }

    const translatedTitle: string = this.translate.instant(
      `menu.${formattedTitle}`
    );

    let fullNewTitle = environment.core.title;
    if (!!translatedTitle && !translatedTitle.includes('menu.')) {
      fullNewTitle = environment.core.title + ' - ' + translatedTitle;
    }

    this.titleService.setTitle(fullNewTitle);
  }
}
